import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, CardBody } from 'reactstrap';

import withLoader from 'components/organisms/withLoader';
import PatientDxEmr from 'components/templates/rpm/patients/patient/patientDxEmr';
import PatientInfo from 'components/templates/rpm/patients/patient/patientInfo';
import EditProfileModal from 'components/templates/rpm/patients/patient/patientInfo/modal';
import CareAgents from 'components/molecules/careAgents/careAgents';
import ClinicRunBookLogs from 'components/templates/admin/patient/components/clinicRunBookLogs';
import PatientStatusLogs from 'components/templates/admin/patient/components/statusLogs';
import PatientCcmStatusLogs from 'components/templates/admin/patient/components/CcmStatusLogs';
import PatientHoldingStatusLogs from 'components/templates/admin/patient/components/holdingStatusLogs';
import GeneralComments from 'components/templates/rpm/patients/patient/generalComments';
import useUser from 'utils/useUser';
import { isBiller, isCcmActionAllowedStatus } from 'utils/userTypeHelper';
import Messages from 'components/templates/rpm/patients/patient/messages';
import EditDaycareModal from './patientDaycare/modal/modal';
import PatientDoctorsInfo from './patientDoctorsInfo';
import PatientDaycare from './patientDaycare';

const UserProfile = ({
  patientGuid,
  patientDetailsLoading,
  patient,
  isAdminPanel = false,
  showMessages,
}) => {
  const loading = useSelector((state) => state.loading.effects.patient.submit);
  const loadingCareLocations = useSelector(
    (state) => state.loading.effects.ccmPatientDetails.submitAllCareLocations,
  );
  const careAgentsLoading = useSelector(
    (state) => state.loading.effects.adminPanelPatients.getPatient,
  );
  const careAgents = useSelector(
    (state) => state.adminPanelPatients.patient.careAgents,
  );
  const careLocationsLoading = useSelector(
    (state) => state.loading.effects.ccmPatientDetails.getCareLocations,
  );
  const careLocations = useSelector(
    (state) => state.ccmPatientDetails.careLocations,
  );
  const { roleNames } = useUser();
  const {
    patient: { submit: submitProfile },
    ccmPatientDetails: { getCareLocations, submitAllCareLocations },
  } = useDispatch();

  useEffect(() => {
    getCareLocations(patientGuid);
  }, [patientGuid]);

  const [profilePopupOpen, setProfilePopupOpen] = useState(false);
  const [careLocationPopupOpen, setCareLocationPopupOpen] = useState(false);
  const canEditPage = isAdminPanel
    ? true
    : isCcmActionAllowedStatus(patient?.profile?.ccmStatus);

  return (
    <div className="user-profile-block">
      <Card className="card-border">
        <CardBody>
          <h3>Patient Profile</h3>
          <div className="user-profile-block-body ccm-table-section">
            <Row>
              <Col md="12" lg="6" xl="4">
                {withLoader(
                  patientDetailsLoading,
                  <PatientInfo
                    profile={patient.profile}
                    handleEditClick={() => setProfilePopupOpen(true)}
                    showEditButton={canEditPage}
                  />,
                )}
              </Col>
              <Col md="12" lg="6" xl="4">
                {withLoader(
                  patientDetailsLoading,
                  <PatientDxEmr
                    diagnosis={patient.diagnosis}
                    emr={patient.emr}
                  />,
                )}
                {withLoader(
                  careLocationsLoading,
                  careLocations && (
                    <PatientDaycare
                      careLocations={careLocations}
                      handleEditClick={() => setCareLocationPopupOpen(true)}
                    />
                  ),
                )}
              </Col>
              <Col md="12" lg="6" xl="4">
                <GeneralComments
                  patientGuid={patientGuid}
                  profile={patient.profile}
                  isCcmPanel
                />
                {showMessages && (
                  <Messages patientGuid={patientGuid} isCcmPanel />
                )}
                {withLoader(
                  patientDetailsLoading,
                  <PatientDoctorsInfo
                    doctor={patient.doctor}
                    clinic={patient.clinic}
                    agency={patient.agency}
                  />,
                )}
                <ClinicRunBookLogs clinicGuid={patient?.clinic?.clinicGuid} />
                {withLoader(
                  careAgentsLoading,
                  <CareAgents agents={careAgents} displayProfType />,
                )}
                <PatientStatusLogs patientGuid={patientGuid} />
                <PatientCcmStatusLogs patientGuid={patientGuid} />
                {!isBiller(roleNames) && (
                  <PatientHoldingStatusLogs patientGuid={patientGuid} />
                )}
              </Col>
            </Row>
          </div>
        </CardBody>
      </Card>
      <EditProfileModal
        isOpen={profilePopupOpen}
        toggle={setProfilePopupOpen}
        patientGuid={patientGuid}
        loading={loading}
        submit={submitProfile}
        profile={patient.profile}
        isRpmPanel={false}
      />
      <EditDaycareModal
        isOpen={careLocationPopupOpen}
        toggle={setCareLocationPopupOpen}
        patientGuid={patientGuid}
        loading={loadingCareLocations}
        submit={submitAllCareLocations}
        locations={careLocations}
        getData={getCareLocations}
      />
    </div>
  );
};

UserProfile.propTypes = {
  patientGuid: PropTypes.string.isRequired,
  patientDetailsLoading: PropTypes.bool.isRequired,
  patient: PropTypes.shape(),
  isAdminPanel: PropTypes.bool,
  showMessages: PropTypes.bool,
};

export default UserProfile;
